import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  getUnidadesAdminControllerExtratoQueryKey,
  getUnidadesAdminControllerListarQueryKey,
  useUnidadesAdminControllerAjustar,
  useUnidadesAdminControllerCreditar,
} from '@/api/generated/admin-unidades/admin-unidades';
import type { SaldoDeAmbienteResponse } from '@/api/generated/model';
import { aplicarErrosDoServidor } from '@/api/erros';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { toast } from '@/shared/ui/sonner';
import { Textarea } from '@/shared/ui/textarea';

const TETO = 100_000;

export type TipoDeLancamento = 'creditar' | 'ajustar';

const schema = z.object({
  quantidade: z.coerce
    .number()
    .int('Informe um número inteiro')
    .min(-TETO)
    .max(TETO)
    .refine((v) => v !== 0, 'A quantidade não pode ser zero'),
  descricao: z.string().trim().min(3, 'Explique o motivo — é o que fica no extrato').max(500),
});

type Formulario = z.input<typeof schema>;

/**
 * Um schema só, com a diferença entre crédito e ajuste no `superRefine`.
 *
 * Dois schemas dariam dois tipos de formulário — e o `useForm` teria de
 * escolher um, que é como se chega em um `as never` na assinatura do resolver.
 */
function resolverPara(tipo: TipoDeLancamento) {
  return zodResolver(
    schema.superRefine((valores, ctx) => {
      if (tipo === 'creditar' && valores.quantidade < 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['quantidade'],
          message: 'O crédito precisa ser positivo. Para descontar, use "Ajustar".',
        });
      }
    }),
  );
}

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  tipo: TipoDeLancamento;
  ambiente: SaldoDeAmbienteResponse;
}

/**
 * Crédito e ajuste de unidades.
 *
 * ⚠️ A descrição é obrigatória, e não é burocracia: o ledger é append-only, o
 * lançamento nunca vai poder ser editado nem apagado, e daqui a seis meses a
 * única resposta para "por que este ambiente ganhou 500 unidades?" é este texto.
 */
export function LancamentoDialogo({ aberto, aoFechar, tipo, ambiente }: Props) {
  const queryClient = useQueryClient();
  const creditando = tipo === 'creditar';

  const creditar = useUnidadesAdminControllerCreditar();
  const ajustar = useUnidadesAdminControllerAjustar();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({ resolver: resolverPara(tipo) });

  useEffect(() => {
    if (aberto) reset({ quantidade: '', descricao: '' });
  }, [aberto, tipo, reset]);

  const quantidade = Number(watch('quantidade'));
  const previsao = Number.isFinite(quantidade) ? ambiente.saldo + quantidade : ambiente.saldo;

  const aoEnviar = handleSubmit(async (bruto) => {
    const dados = schema.parse(bruto);

    try {
      const variaveis = { tenantId: ambiente.tenantId, data: dados };
      if (creditando) await creditar.mutateAsync(variaveis);
      else await ajustar.mutateAsync(variaveis);

      await queryClient.invalidateQueries({ queryKey: getUnidadesAdminControllerListarQueryKey() });
      await queryClient.invalidateQueries({
        queryKey: getUnidadesAdminControllerExtratoQueryKey(ambiente.tenantId),
      });

      toast.success(creditando ? 'Unidades creditadas.' : 'Ajuste lançado.');
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(
        erro,
        setError,
        creditando ? 'Não foi possível creditar.' : 'Não foi possível ajustar.',
      );
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {creditando ? 'Creditar unidades' : 'Ajustar saldo'} — {ambiente.tenantNome}
          </DialogTitle>
          <DialogDescription>
            {creditando
              ? 'O lançamento entra no extrato e não pode ser editado nem apagado depois.'
              : 'Ajuste corrige o saldo para mais ou para menos. Também é definitivo.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={aoEnviar} className="space-y-4" noValidate>
          {errors.root?.message && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <Campo
            id="quantidade"
            rotulo="Quantidade"
            erro={errors.quantidade?.message}
            obrigatorio
            dica={creditando ? undefined : 'Use número negativo para descontar.'}
          >
            <Input
              id="quantidade"
              type="number"
              inputMode="numeric"
              className={CLASSE_CONTROLE}
              {...register('quantidade')}
            />
          </Campo>

          <div className="rounded-lg bg-muted/50 px-4 py-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Saldo atual</span>
              <span className="text-foreground">{ambiente.saldo}</span>
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-muted-foreground">Depois do lançamento</span>
              <span className={previsao < 0 ? 'text-destructive font-medium' : 'text-brand font-medium'}>
                {previsao}
              </span>
            </div>
            {previsao < 0 && (
              <p className="text-xs text-destructive mt-2">
                O servidor recusa: o saldo não pode ficar negativo.
              </p>
            )}
          </div>

          <Campo id="descricao" rotulo="Motivo" erro={errors.descricao?.message} obrigatorio>
            <Textarea
              id="descricao"
              rows={3}
              className={CLASSE_CONTROLE}
              placeholder={
                creditando ? 'Ex.: cortesia de boas-vindas' : 'Ex.: estorno de crédito duplicado'
              }
              {...register('descricao')}
            />
          </Campo>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {creditando ? 'Creditar' : 'Lançar ajuste'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
